import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import {
  ButtonComponent,
  ButtonSizeType,
  ModalWindowComponent,
  TableColumn,
  ToastNotificationComponent
} from '@ui/aos-lib';
import { ListingPageService } from 'src/app/shared/components/listing-page/listing-page.service';
import { RequestProcessingService } from '../../services/request-processing.service';
import { RequestProcessingTableRow } from '../../services/request-processing.types';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { ModalService } from 'src/app/services/utility/modal.service';
import { AsyncPipe } from '@angular/common';
import { Observable, of, take } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastNotificationComponent],
  imports: [ButtonComponent, ModalWindowComponent, TranslateModule, AsyncPipe],
  styleUrl: './request-processing-actions-column-cancel.component.scss',
  template: `
    @if ((canCancel$ | async) && item.canCancel === true && item.requestId) {
      <ui-button
        (click)="openConfirmation()"
        [size]="buttonSize"
        [themeColor]="'primary'"
        >{{ cancelLabel | translate }}
      </ui-button>
    }
    <ui-modal-window
      [windowTitleText]="dialogTitle | translate"
      (closeButtonClick)="closeConfirmation()"
    >
      <div class="aos-request-processing-cancel-dialog__content">
        {{ dialogMessage | translate }}
      </div>
      <div class="aos-request-processing-cancel-dialog__footer">
        <ui-button
          id="requestProcessingCancelDismissButton"
          type="button"
          [themeColor]="'secondary'"
          (click)="closeConfirmation()"
          >{{ dismissLabel | translate }}</ui-button
        >
        <ui-button
          id="requestProcessingCancelConfirmButton"
          [themeColor]="'primary'"
          (click)="confirmCancel()"
          >{{ confirmLabel | translate }}</ui-button
        >
      </div>
    </ui-modal-window>
  `
})
export class RequestProcessingActionsColumnCancelComponent implements OnInit {
  @ViewChild(ModalWindowComponent, { static: true })
  private readonly modalWindowComponent!: ModalWindowComponent;

  protected readonly requestProcessingService = inject(RequestProcessingService);
  private readonly listingPageService = inject(ListingPageService);
  private readonly toastNotificationComponent = inject(ToastNotificationComponent);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  protected successNotificationTitle =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.TITLE.REQUEST_CANCEL_SUCCESS;
  protected successNotificationDetail =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.DETAIL.REQUEST_CANCEL_SUCCESS;
  protected failedNotificationTitle =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.TITLE.REQUEST_CANCEL_FAILED;
  protected failedNotificationDetail =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.DETAIL.REQUEST_CANCEL_FAILED;
  protected readonly cancelLabel = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.BUTTON.CANCEL;
  protected readonly confirmLabel = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.BUTTON.CANCEL_CONFIRM;
  protected readonly dismissLabel = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.BUTTON.CANCEL_DISMISS;
  protected readonly dialogTitle = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.CANCEL_DIALOG.TITLE;
  protected readonly dialogMessage = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.CANCEL_DIALOG.MESSAGE;

  @Input() item!: RequestProcessingTableRow;
  @Input() column!: TableColumn;

  protected readonly buttonSize: ButtonSizeType = 'small';
  protected canCancel$: Observable<boolean> = of(false);

  ngOnInit(): void {
    this.canCancel$ = this.requestProcessingService.canCancel$(this.item.commandType);
    this.modalService.register(this.modalWindowComponent, this.destroyRef);
  }

  protected openConfirmation(): void {
    this.modalWindowComponent.open({
      ...(this.modalService.containerRef() && { appendTo: this.modalService.containerRef()! })
    });
  }

  protected closeConfirmation(): void {
    this.modalWindowComponent.close();
  }

  protected confirmCancel(): void {
    this.closeConfirmation();
    this.cancel();
  }

  protected cancel(): void {
    const requestId = this.item.requestId;
    if (!requestId) {
      return;
    }

    this.requestProcessingService
      .cancelRequest(requestId)
      .pipe(take(1))
      .subscribe({
        next: canceled => {
          if (canceled === true) {
            const toastTitle = this.translate.instant(this.successNotificationTitle);
            const toastDetail = this.translate.instant(this.successNotificationDetail);

            this.toastNotificationComponent.showSuccessToast(toastTitle, toastDetail);
            this.listingPageService.refresh();
            return;
          }

          const toastTitle = this.translate.instant(this.failedNotificationTitle);
          const toastDetail = this.translate.instant(this.failedNotificationDetail);
          this.toastNotificationComponent.showErrorToast(toastTitle, toastDetail);
        }
      });
  }
}
